import { Head } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { creatorListingsPath, productCreatorStagePath } from "@travily/routes";
import type ListingsCreatorPriceAmount from "@travily/types/pages/ListingsCreatorPriceAmount";
import { subunitFactor } from "@ui/lib/locale-data";
import { useCreatorTopBarActions } from "@/lib/wizard/use-creator-top-bar-actions";
import { useWizardForm } from "@/lib/wizard/use-wizard-form";
import { ProductPriceAmountStepView } from "./show.view";

type PageProps = ListingsCreatorPriceAmount;

type FormData = {
  price_amount: string;
};

// Final pricing stage: the amount, with the take-home ledger — rendered in the
// seller currency (set on the preceding selling_country step), so the price and
// what they keep are always in their own money, never a USD default. The £/€/$
// symbol carries the currency; Back returns to change the country.
const ProductPriceAmountStep = ({
  listing_id,
  stage_index,
  stage_order,
  price_cents,
  price_currency,
  currency,
  platform_fee_rate,
}: PageProps) => {
  const factor = subunitFactor(currency);
  // Only prefill when the saved amount is in the SAME currency we are now pricing
  // in. A listing frozen in USD before the seller set a non-USD country starts
  // empty, so an old amount is never silently reinterpreted as the new currency.
  const initialAmount = price_cents !== null && price_currency === currency ? (price_cents / factor).toString() : "";

  const { form, fieldError, currentStage, totalStages, onBack, onPrimary, primaryPending } = useWizardForm<FormData>({
    routes: {
      stageUrl: (stage) => productCreatorStagePath(listing_id, stage),
      entryUrl: creatorListingsPath(),
    },
    stageName: "price_amount",
    stageIndex: stage_index,
    stageOrder: stage_order,
    initialData: { price_amount: initialAmount },
    transform: (data) => ({ price_cents: Math.round(Number.parseFloat(data.price_amount) * factor) }),
  });

  const amountValue = Number.parseFloat(form.data.price_amount);
  const priceError = fieldError("price_cents");

  const topBarActions = useCreatorTopBarActions({ saveHref: creatorListingsPath() });

  return (
    <>
      <Head title={t("web.products.creator.steps.price_amount.show.meta_title")}>
        <meta name="description" content={t("web.products.creator.steps.price_amount.show.meta_description")} />
      </Head>
      <ProductPriceAmountStepView
        currentStage={currentStage}
        totalStages={totalStages}
        currency={currency}
        platformFeeRate={platform_fee_rate}
        value={form.data.price_amount}
        priceCents={amountValue > 0 ? Math.round(amountValue * factor) : null}
        error={priceError}
        primaryPending={primaryPending}
        topBarActions={topBarActions}
        onBack={onBack}
        onPrimary={onPrimary}
        onChange={(value) => form.setData("price_amount", value)}
      />
    </>
  );
};

export default ProductPriceAmountStep;
